import config from "@/libs/config";
import { useCallback, useContext } from "react";
import { useRouter } from "next/router";
import { useForm, FormProvider } from "react-hook-form";
import type Rollbar from "rollbar";
import { MainTemplate } from "@/components/templates";
import { useSnackBar } from "@moneyforward/mfx_biz_react_components";
import { useRegisterMemberWithConnectBoardMutation } from "@/generated/graphql";
import { ConnectBoardRegisterUserForm } from "@/components/organisms";
import { AuthContext, SessionInfo } from "@/contexts/AuthContext";
import { PageTitle } from "@/components/molecules";
import { getUrlWithQuery } from "@/libs/url";
import { ErrorCode, errorCodeIsIncluded, moveToErrorPage } from "@/libs/errors";
import { ApolloError } from "@apollo/client";
import { setLoginSource, LoginSource } from "@/libs/loginSource";
import useRollbarUtil from "../hooks/useRollbarUtil";

type FormValues = {
  lastName: string;
  lastNameKana: string;
  middleName: string;
  middleNameKana: string;
  firstName: string;
  firstNameKana: string;
  // email?: string;
  // password?: string;
};

export const ConnectBoardRegisterUserPage = () => {
  const { rollbar } = useRollbarUtil();
  const authContext = useContext(AuthContext);
  const { notify } = useSnackBar();

  const router = useRouter();
  if (!router.isReady) {
    return <div></div>;
  }
  const { app, code } = router.query;
  const registrationCode = Array.isArray(code) ? code[0] : code;

  // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: fix to comply with the rules of react-hooks as not following them may lead to unintended side effects
  const methods = useForm<FormValues>({
    reValidateMode: "onBlur",
    defaultValues: {
      middleName: "",
      middleNameKana: "",
      // email: "",
      // password: "",
    },
  });

  const signIn = async (token: {
    access_token: string;
    id_token: string;
    refresh_token: string;
    token_type: string;
  }) => {
    try {
      const session: SessionInfo = {
        accessToken: token.access_token,
        idToken: token.id_token,
        refreshToken: token.refresh_token,
      };
      await authContext.setSignInUserSession(session);
      setLoginSource(LoginSource.connectboard);
      // TODO: notify
      router.push(getUrlWithQuery("/", app));
    } catch (e) {
      rollbar.error("Failed to set session", e);
      notify("ログインに失敗しました");
      setLoginSource(LoginSource.none);
      moveToErrorPage(ErrorCode.CONNBOARD_INTERNAL_SERVER_ERROR);
    }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: fix to comply with the rules of react-hooks as not following them may lead to unintended side effects
  const [registerMember] = useRegisterMemberWithConnectBoardMutation({
    onCompleted: (data) => {
      const token = data.registerMemberWithConnectBoard.token;
      if (!token) {
        moveToErrorPage(ErrorCode.CONNBOARD_INTERNAL_SERVER_ERROR);
        return;
      }
      signIn(token);
    },
    onError: (err) => {
      handleError(rollbar, "Failed to register member with connect board")(err);
    },
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: fix to comply with the rules of react-hooks as not following them may lead to unintended side effects
  const handleSubmit = useCallback(
    async (v: FormValues) => {
      const client_id = config.COGNITO_CLIENT_ID;
      await registerMember({
        variables: {
          input: {
            client_id,
            registrationCode,
            ...v,
          },
        },
      });
    },
    [registerMember]
  );

  return (
    <MainTemplate title={`新規登録`} disableAccountDropDown={true}>
      <>
        <PageTitle
          mainTitle={`新規登録`}
          subTitle={<>ユーザー情報を入力して登録を開始してください</>}
        />
        <FormProvider {...methods}>
          <ConnectBoardRegisterUserForm onSubmit={handleSubmit} />
        </FormProvider>
      </>
    </MainTemplate>
  );
};

const handleError =
  (rollbar: Rollbar, messageToRollbar: string) => (err: ApolloError) => {
    const errorList: Array<ErrorCode> = [
      // FIXME: 他のパターンがないか確認する
      ErrorCode.CONNBOARD_INVALID_REGISTRATION_TOKEN,
    ];

    errorList.forEach((code) => {
      if (errorCodeIsIncluded(err, code)) {
        moveToErrorPage(code);
        return;
      }
    });

    // 上記にないエラーでもconnectboard ボタンのあるエラーページに遷移する
    // username はランダム発番するため、USERNAME_EXISTS はInternalError として扱う
    rollbar.error(messageToRollbar, err);
    moveToErrorPage(ErrorCode.CONNBOARD_INTERNAL_SERVER_ERROR);
  };
